// Schema is also defined in extension/content-data.js — keep both in sync (#14).
//
// SCHEMA COUPLING: if you change the v6 schema here, also update IDB_VERSION
// in extension/crm-mappings.js. Both files must agree on the schema.
import Dexie, { type EntityTable, type Table } from 'dexie'

export interface Participant {
  name: string
  headline: string
  profileImage: string | null
  profileUrn: string | null
  // Lean enrichment fields — written to IDB for instant display
  location?: string | null
  connectedAt?: number | null
  publicIdentifier?: string | null
  currentTitle?: string | null
  currentCompany?: string | null
  _enrichedAt?: number  // timestamp — presence means enrichment was attempted
}

export interface Conversation {
  conversationUrn: string
  participantName: string
  participantHeadline: string
  participantProfileImage: string | null
  participantProfileUrn: string
  allParticipants: Participant[]
  isGroup: boolean
  lastMessageText: string
  lastMessageTimestamp: number
  lastMessageSenderUrn: string
  /**
   * Whether the most recent message was sent by the viewer (`this.profileUrn`
   * on the extension's MessagingEngine). Written at sync-time by the extension;
   * absent for:
   *   - Rows written by older extension versions (pre-0.4 — see
   *     docs/adr/0002-write-time-conversation-denormalization.md)
   *   - Conversations with no messages yet
   *   - Sales Nav conversations where the viewer's sales identity wasn't
   *     resolvable at write time
   *
   * Filters that depend on this field (Waiting for their reply / Waiting for
   * my reply) check `=== true` / `=== false` explicitly so `undefined` rows
   * naturally drop out of both views rather than matching either.
   */
  lastMessageFromMe?: boolean
  createdAt: number
  isRead: boolean
  unreadCount: number
  category: string
  /** Stored on archive so unarchive can restore to the original category. */
  previousCategory?: string
  conversationType: string | null
  threadMuted: boolean
  _cachedAt: number
}

export interface Message {
  messageUrn: string
  entityUrn?: string | null
  conversationUrn: string
  senderName: string
  senderProfileUrn: string
  isOutgoing: boolean
  text: string
  timestamp: number
  attachments: { type: string; name?: string; url?: string; size?: number; title?: string; description?: string; thumbnail?: string }[]
  reactions: { emoji: string; count: number; viewerReacted: boolean }[]
  mentions?: { start: number; length: number; profileUrn: string }[]
  label: string | null
  isSponsored: boolean
  _cachedAt: number
}

export interface CachedInvite {
  invitationId: string
  name: string
  headline: string
  sentTimeLabel: string | null
  sharedSecret: string | null
  entityUrn: string | null
  // The PERSON's profile URN (urn:li:fsd_profile:...). Distinct from entityUrn
  // which is the INVITATION's URN. Added for CRM matching on accept.
  profileUrn?: string | null
  profileImage: string | null
  mutualConnections: string | null
  profileUrl: string | null
  message?: string
  category: string
  _cachedAt: number
}

export interface CrmMessageSync {
  messageUrn: string              // LinkedIn URN, PK part 1
  crmName: 'affinity'             // PK part 2
  interactionId: number           // Affinity interaction ID that contains this message
  burstId: string                 // Synthetic UUID grouping messageUrns in the same burst
  syncedAt: number
}

export type CrmInviteEventType = 'sent' | 'accepted' | 'withdrawn' | 'declined' | 'expired'

export interface CrmInviteSync {
  profileUrn: string              // LinkedIn URN, PK part 1
  eventType: CrmInviteEventType   // PK part 2
  crmName: 'affinity'             // PK part 3
  affinityNoteId: number          // Affinity note ID returned from POST /notes
  syncedAt: number
  eventAt: number                 // When the original LinkedIn event happened
}

export interface CrmMapping {
  profileUrn: string              // LinkedIn URN (e.g., "urn:li:fsd_profile:ACoAAB...")
  crmName: 'affinity'             // Narrow union — expand when Attio lands
  crmRecordId: string             // Affinity person ID (stringified number), empty when no match
  crmContactName: string          // Cached for display
  crmContactEmail: string | null  // Cached primary email for display
  status: 'matched' | 'no_match' | 'multiple_matches' | 'created' | 'manual_skip'
  matchMethod:
    | 'email'
    | 'name_plus_company'
    | 'linkedin_url'
    | 'name_exact'
    | 'manual'
    | 'created'
    | null
  linkedinSlug: string | null     // Vanity slug (e.g., "danieljarjoura")
  rawName: string                 // Name as LinkedIn gave us (may be truncated like "Suleiman M.")
  searchedName: string            // Name we actually used for matching (post-reconstruction)
  // Phase 4 will cache per-field field_value IDs here so it can PUT updates
  // instead of POSTing. Null until enrichment writes first run.
  fieldValueIds: Record<string, number> | null
  updatedAt: number
  // Only present when status === 'multiple_matches'. Cleared when the user picks.
  candidates?: Array<{
    crmRecordId: string
    name: string
    email: string | null
    currentOrganization: string | null
  }>
}

const db = new Dexie('fwdslash') as Dexie & {
  conversations: EntityTable<Conversation, 'conversationUrn'>
  messages: EntityTable<Message, 'messageUrn'>
  invites: EntityTable<CachedInvite, 'invitationId'>
  sentInvitationTimestamps: EntityTable<{ profileUrn: string; sentAt: number }, 'profileUrn'>
  crmMappings: Table<CrmMapping, [string, string]>  // compound key: [profileUrn, crmName]
  crmMessageSync: Table<CrmMessageSync, [string, string]>  // compound key: [messageUrn, crmName]
  crmInviteSync: Table<CrmInviteSync, [string, string, string]>  // compound key: [profileUrn, eventType, crmName]
}

db.version(1).stores({
  conversations: 'conversationUrn, category, lastMessageTimestamp, participantProfileUrn',
  messages: 'messageUrn, conversationUrn, timestamp',
})

db.version(2).stores({
  conversations: 'conversationUrn, category, lastMessageTimestamp, participantProfileUrn',
  messages: 'messageUrn, conversationUrn, timestamp',
  invites: 'invitationId, category',
})

db.version(3).stores({
  conversations: 'conversationUrn, category, lastMessageTimestamp, participantProfileUrn',
  messages: 'messageUrn, conversationUrn, timestamp',
  invites: 'invitationId, category',
  sentInvitationTimestamps: 'profileUrn',
})

// v4 and v5: no schema changes — version bumps to stay in sync with the
// extension's IDB version during development.
db.version(4).stores({
  conversations: 'conversationUrn, category, lastMessageTimestamp, participantProfileUrn',
  messages: 'messageUrn, conversationUrn, timestamp',
  invites: 'invitationId, category',
  sentInvitationTimestamps: 'profileUrn',
})

db.version(5).stores({
  conversations: 'conversationUrn, category, lastMessageTimestamp, participantProfileUrn',
  messages: 'messageUrn, conversationUrn, timestamp',
  invites: 'invitationId, category',
  sentInvitationTimestamps: 'profileUrn',
})

// =============================================================================
// SCHEMA VERSION ASSERTION
// =============================================================================
// The db.on('ready') hook below fires if Dexie opens at a different version
// than we declared — useful for catching in-file mistakes (bumped one
// db.version(N) call but not the DECLARED_VERSION constant here).
//
// LIMITATION: this does NOT catch drift between extension/idb-version.js
// (FWDSLASH_IDB_VERSION) and this DECLARED_VERSION. Those are still coupled
// by convention. Any schema change is a three-point update:
//   1. extension/idb-version.js — bump FWDSLASH_IDB_VERSION
//   2. this file — bump DECLARED_VERSION, add a new db.version(N).stores(...) block
//   3. extension/content-data.js — handle the new stores in onupgradeneeded
//
// A future improvement: have inject-id.js expose the extension's IDB version on
// the sentinel element, then the web app can compare at runtime.
// =============================================================================
const DECLARED_VERSION = 11

db.version(6).stores({
  conversations: 'conversationUrn, category, lastMessageTimestamp, participantProfileUrn',
  messages: 'messageUrn, conversationUrn, timestamp',
  invites: 'invitationId, category',
  sentInvitationTimestamps: 'profileUrn',
  crmMappings: '[profileUrn+crmName], profileUrn, crmName, status, updatedAt',
})

db.version(7).stores({
  conversations: 'conversationUrn, category, lastMessageTimestamp, participantProfileUrn',
  messages: 'messageUrn, conversationUrn, timestamp',
  invites: 'invitationId, category',
  sentInvitationTimestamps: 'profileUrn',
  crmMappings: '[profileUrn+crmName], profileUrn, crmName, status, updatedAt',
  crmMessageSync: '[messageUrn+crmName], messageUrn, crmName, interactionId, burstId, syncedAt',
})

db.version(8).stores({
  conversations: 'conversationUrn, category, lastMessageTimestamp, participantProfileUrn',
  messages: 'messageUrn, conversationUrn, timestamp',
  invites: 'invitationId, category',
  sentInvitationTimestamps: 'profileUrn',
  crmMappings: '[profileUrn+crmName], profileUrn, crmName, status, updatedAt',
  crmMessageSync: '[messageUrn+crmName], messageUrn, crmName, interactionId, burstId, syncedAt',
})

db.version(9).stores({
  conversations: 'conversationUrn, category, lastMessageTimestamp, participantProfileUrn',
  messages: 'messageUrn, conversationUrn, timestamp',
  invites: 'invitationId, category',
  sentInvitationTimestamps: 'profileUrn',
  crmMappings: '[profileUrn+crmName], profileUrn, crmName, status, updatedAt',
  crmMessageSync: '[messageUrn+crmName], messageUrn, crmName, interactionId, burstId, syncedAt',
  crmInviteSync: '[profileUrn+eventType+crmName], profileUrn, crmName, eventType, syncedAt',
})

// v10: no new stores on web side — schema unchanged. Version bump mirrors the
// extension adding conversations + messages stores to its own IDB in idb-schema.js.
db.version(10).stores({
  conversations: 'conversationUrn, category, lastMessageTimestamp, participantProfileUrn',
  messages: 'messageUrn, conversationUrn, timestamp',
  invites: 'invitationId, category',
  sentInvitationTimestamps: 'profileUrn',
  crmMappings: '[profileUrn+crmName], profileUrn, crmName, status, updatedAt',
  crmMessageSync: '[messageUrn+crmName], messageUrn, crmName, interactionId, burstId, syncedAt',
  crmInviteSync: '[profileUrn+eventType+crmName], profileUrn, crmName, eventType, syncedAt',
})

// v11: adds linkedinSlug to the crmMappings index list. The SW's
// getAllMappingsWithSlug (invoked from the invite-accepted matcher) used to
// full-scan the store; Dexie's schema string needs the index in web-side too
// so a web-side query using .where('linkedinSlug').equals(...) would be
// indexed, and to stay in sync with the SW-side IDB version.
db.version(DECLARED_VERSION).stores({
  conversations: 'conversationUrn, category, lastMessageTimestamp, participantProfileUrn',
  messages: 'messageUrn, conversationUrn, timestamp',
  invites: 'invitationId, category',
  sentInvitationTimestamps: 'profileUrn',
  crmMappings: '[profileUrn+crmName], profileUrn, crmName, linkedinSlug, status, updatedAt',
  crmMessageSync: '[messageUrn+crmName], messageUrn, crmName, interactionId, burstId, syncedAt',
  crmInviteSync: '[profileUrn+eventType+crmName], profileUrn, crmName, eventType, syncedAt',
})

db.on('ready', () => {
  if (db.verno !== DECLARED_VERSION) {
    console.error(
      `[FwdSlash DB] Version mismatch — declared ${DECLARED_VERSION}, opened ${db.verno}. ` +
      `Check extension/idb-version.js agrees with db.ts.`,
    )
  }
})

export { db }
